import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { saveGastoCompra } from '../../../../../firebase/facturacionUtils';
import { useFacturacionData } from '../../../../../context/FacturacionContext';
import { formatNumberToInput } from '../../helpers';

const parseMontoSeguro = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const limpio = String(val).replace(/\./g, '').replace(',', '.');
    return parseFloat(limpio) || 0;
};

const getFechaHoy = () => new Date().toISOString().substring(0, 10);

// --- MODIFICADO: Acepta fechaGasto opcional en formato YYYY-MM-DD ---
const getPeriodoActual = (fechaStr) => {
    if (!fechaStr) {
        return `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
    }
    const partes = String(fechaStr).substring(0, 10).split('-');
    if (partes.length === 3) {
        const [anio, mes] = partes;
        return `${mes.padStart(2, '0')}/${anio}`;
    }
    return `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;
};

const INITIAL_FORM_DATA = {
    fechaIngreso: getFechaHoy(),
    fechaGasto: getFechaHoy(),
    fechaVencimientoGasto: getFechaHoy(),
    periodo: getPeriodoActual(getFechaHoy()),
    tipoComprobante: '',
    numeroComprobante: '',
    proveedorId: '',
    proveedorNombre: '',
    areaId: '',
    areaNombre: '',
    tipoGasto: '',
    categoriaGasto: '',
    conceptoId: '',
    conceptoNombre: '',
    subconceptoNombre: '',
    moneda: 'ARS',
    montoNeto: '',
    tasaIva: 21,
    incluyePercepciones: false,
    percepcionesList: [],
    incluyeOtrosTributos: false,
    montoOtrosTributos: '',
    montoEgresoBanco: '',
    forzarConciliacion: false,
    formaPagoImpuesto: '',
    cuentaId: '',
    cuentaNombre: '',
    esPlanPago: false,
    cuotasPlan: [],
    estatus: 'Pendiente de Pago'
};

const INITIAL_PAGO_INPUT = { forma: '', monto: '', cuentaId: '' };
const INITIAL_CHEQUE_INPUT = { numero: '', banco: '', monto: '', vencimiento: '' };
const INITIAL_CUOTA_INPUT = { fecha: '', monto: '' };

export const useModalGastoCompra = ({ show, onClose, onSuccess, initialData = null, maestrosProp = {} }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { facturacionMasters } = useFacturacionData();

    const proveedores = facturacionMasters?.proveedores?.length > 0 ? facturacionMasters.proveedores : (maestrosProp.proveedores || []);
    const cuentas = facturacionMasters?.cuentas?.length > 0 ? facturacionMasters.cuentas : (maestrosProp.cuentas || []);
    const areas = facturacionMasters?.areas?.length > 0 ? facturacionMasters.areas : (maestrosProp.areas || []);
    const conceptosGastos = facturacionMasters?.conceptosGastos?.length > 0 ? facturacionMasters.conceptosGastos : (maestrosProp.conceptosGastos || maestrosProp.conceptos || []);

    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [subconceptosDisponibles, setSubconceptosDisponibles] = useState([]);
    
    const [pagosList, setPagosList] = useState([]);
    const [pagoInput, setPagoInput] = useState(INITIAL_PAGO_INPUT);

    const [chequesList, setChequesList] = useState([]);
    const [chequesBorrador, setChequesBorrador] = useState([]);
    const [chequeInput, setChequeInput] = useState(INITIAL_CHEQUE_INPUT);

    const [cuotasBorrador, setCuotasBorrador] = useState([]);
    const [cuotaInput, setCuotaInput] = useState(INITIAL_CUOTA_INPUT);

    const prevShowRef = useRef(show);

    useEffect(() => {
        if (show && (!prevShowRef.current || initialData?.id !== formData.id)) {
            setPagoInput(INITIAL_PAGO_INPUT);
            setChequeInput(INITIAL_CHEQUE_INPUT);
            setChequesBorrador([]);
            setCuotaInput(INITIAL_CUOTA_INPUT);
            setCuotasBorrador([]);

            if (initialData) {
                const comp = initialData.tipoComprobante || initialData.tipoComprobanteGasto || '';
                const esModoDirecto = comp === 'Impuestos / Tasas Fiscales' || comp === 'Préstamo';
                const fechaGastoInit = initialData.fechaGasto || getFechaHoy();

                setFormData({
                    id: initialData.id,
                    fechaIngreso: initialData.fechaIngreso || getFechaHoy(),
                    fechaGasto: fechaGastoInit,
                    fechaVencimientoGasto: initialData.fechaVencimientoGasto || fechaGastoInit || getFechaHoy(),
                    // --- MODIFICADO: Calcula el período con base en fechaGasto si no viene explícito ---
                    periodo: initialData.periodo || getPeriodoActual(fechaGastoInit),
                    tipoComprobante: comp,
                    numeroComprobante: initialData.numeroComprobante || '',
                    proveedorId: initialData.proveedorId || '',
                    proveedorNombre: initialData.proveedorNombre || '',
                    areaId: initialData.areaId || '',
                    areaNombre: initialData.areaNombre || '',
                    tipoGasto: initialData.tipoGasto || '',
                    categoriaGasto: initialData.categoriaGasto || '',
                    conceptoId: initialData.conceptoId || initialData.conceptoGastoId || '',
                    conceptoNombre: initialData.conceptoNombre || initialData.conceptoGastoNombre || '',
                    subconceptoNombre: initialData.subconceptoNombre || '',
                    moneda: initialData.moneda || 'ARS',
                    montoNeto: formatNumberToInput(initialData.montoNeto || initialData.montoTotal),
                    tasaIva: esModoDirecto ? 0 : (initialData.tasaIva !== undefined ? initialData.tasaIva : 21),
                    incluyePercepciones: !!initialData.incluyePercepciones || (Array.isArray(initialData.percepcionesList) && initialData.percepcionesList.length > 0),
                    percepcionesList: Array.isArray(initialData.percepcionesList) ? initialData.percepcionesList : [],
                    incluyeOtrosTributos: !!initialData.incluyeOtrosTributos || parseMontoSeguro(initialData.montoOtrosTributos) > 0,
                    montoOtrosTributos: formatNumberToInput(initialData.montoOtrosTributos),
                    montoEgresoBanco: formatNumberToInput(initialData.montoEgresoBanco),
                    forzarConciliacion: !!initialData.forzarConciliacion,
                    formaPagoImpuesto: initialData.formaPagoImpuesto || initialData.formaPago || '',
                    cuentaId: initialData.cuentaId || initialData.cuentaIdOrigen || '',
                    cuentaNombre: initialData.cuentaNombre || initialData.cuentaNombreOrigen || '',
                    esPlanPago: !!initialData.esPlanPago,
                    estatus: initialData.estatus || 'Pendiente de Pago',
                    cuotasPlan: Array.isArray(initialData.cuotasPlan) ? initialData.cuotasPlan.map(c => ({
                        numero: c.numero,
                        monto: c.monto,
                        fechaVencimiento: c.fechaVencimiento,
                        cuentaId: c.cuentaId || '',
                        cuentaNombre: c.cuentaNombre || '',
                        pagada: !!c.pagada
                    })) : []
                });

                if (Array.isArray(initialData.pagosList)) {
                    setPagosList(initialData.pagosList.filter(p => p.formaPago !== 'Cheque').map(p => ({ ...p, pagado: !!p.pagado })));
                }
                if (Array.isArray(initialData.chequesList)) {
                    setChequesList(initialData.chequesList.map(c => ({
                        numero: c.numero,
                        monto: c.monto,
                        fechaVencimiento: c.fechaVencimiento,
                        cuentaId: c.cuentaId || c.bancoId || '',
                        cuentaNombre: c.cuentaNombre || c.banco || '',
                        pagada: !!c.pagada
                    })));
                }

                const targetId = initialData.conceptoId || initialData.conceptoGastoId;
                if (targetId) {
                    const found = conceptosGastos.find(c => c.id === targetId);
                    if (found && Array.isArray(found.subconceptos)) {
                        setSubconceptosDisponibles(found.subconceptos);
                    }
                }
            } else {
                setFormData(INITIAL_FORM_DATA);
                setPagosList([]);
                setChequesList([]);
                setSubconceptosDisponibles([]);
            }
            setError(null);
        }
        prevShowRef.current = show;
    }, [show, initialData, conceptosGastos]);

    // --- AGREGADO: Sincronización automática de periodo al modificar fechaGasto ---
    useEffect(() => {
        if (formData.fechaGasto) {
            const nuevoPeriodo = getPeriodoActual(formData.fechaGasto);
            setFormData(prev => {
                if (prev.periodo !== nuevoPeriodo) {
                    return { ...prev, periodo: nuevoPeriodo };
                }
                return prev;
            });
        }
    }, [formData.fechaGasto]);

    const esImpuesto = useMemo(() => formData.tipoComprobante === 'Impuestos / Tasas Fiscales', [formData.tipoComprobante]);
    const esPrestamo = useMemo(() => formData.tipoComprobante === 'Préstamo', [formData.tipoComprobante]);
    const esModoDirecto = esImpuesto || esPrestamo;

    const calculos = useMemo(() => {
        const neto = parseMontoSeguro(formData.montoNeto);
        const tasa = esModoDirecto ? 0 : (parseFloat(formData.tasaIva) || 0);
        const montoIva = esModoDirecto ? 0 : Math.round((neto * (tasa / 100)) * 100) / 100;
        
        const percepcionesList = formData.incluyePercepciones && Array.isArray(formData.percepcionesList) ? formData.percepcionesList : [];
        const totalPercepciones = percepcionesList.reduce((acc, curr) => acc + (parseFloat(curr.monto) || 0), 0);
        const montoOtrosTributos = formData.incluyeOtrosTributos ? parseMontoSeguro(formData.montoOtrosTributos) : 0;

        const montoTotal = Math.round((neto + montoIva + totalPercepciones + montoOtrosTributos) * 100) / 100;

        const totalPagosSimples = pagosList.reduce((acc, curr) => acc + (parseFloat(curr.monto) || 0), 0);
        const totalChequesAsignados = chequesList.reduce((acc, curr) => acc + (parseFloat(curr.monto) || 0), 0);
        const totalCuotasAsignadas = (formData.cuotasPlan || []).reduce((acc, curr) => acc + (parseFloat(curr.monto) || 0), 0);
        
        const totalPagadoAsignado = Math.round((totalPagosSimples + totalChequesAsignados + totalCuotasAsignadas) * 100) / 100;

        const pagosEfectivos = pagosList.filter(p => p.pagado).reduce((acc, p) => acc + (parseFloat(p.monto) || 0), 0);
        const chequesPagados = chequesList.filter(c => c.pagada).reduce((acc, c) => acc + (parseFloat(c.monto) || 0), 0);
        const cuotasPagadas = (formData.cuotasPlan || []).filter(c => c.pagada).reduce((acc, c) => acc + (parseFloat(c.monto) || 0), 0);

        const egresoRealBanco = Math.round((pagosEfectivos + chequesPagados + cuotasPagadas) * 100) / 100;
        const rawDiff = Math.round((montoTotal - egresoRealBanco) * 100) / 100;
        
        let diferenciaEgreso = rawDiff;
        if (formData.forzarConciliacion) {
            if (egresoRealBanco > 0) diferenciaEgreso = 0;
        } else if (Math.abs(rawDiff) <= 0.05) {
            diferenciaEgreso = 0;
        }

        let estatusCalculado = 'Pendiente de Pago';
        if (egresoRealBanco > 0) {
            estatusCalculado = (diferenciaEgreso === 0 || egresoRealBanco >= montoTotal) ? 'Pagado' : 'Parcialmente Pagado';
        }

        return { 
            neto, montoIva, totalPercepciones, montoOtrosTributos, montoTotal, 
            totalPagadoAsignado, totalChequesAsignados, egresoRealBanco, 
            diferenciaEgreso, tasaFinal: tasa, estatusCalculado
        };
    }, [formData.montoNeto, formData.tasaIva, formData.incluyePercepciones, formData.percepcionesList, formData.incluyeOtrosTributos, formData.montoOtrosTributos, formData.forzarConciliacion, formData.cuotasPlan, esModoDirecto, pagosList, chequesList]);

    // SINCRONIZACIÓN AUTOMÁTICA EN TIEMPO REAL DEL DESPLEGABLE
    useEffect(() => {
        if (calculos.estatusCalculado) {
            setFormData(prev => {
                if (prev.estatus !== calculos.estatusCalculado) {
                    return { ...prev, estatus: calculos.estatusCalculado };
                }
                return prev;
            });
        }
    }, [calculos.estatusCalculado]);

    const handleBlurCurrency = useCallback((field) => {
        const value = formData[field];
        if (!value) return;
        const num = parseMontoSeguro(value);
        if (num > 0) {
            setFormData(prev => ({ ...prev, [field]: num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
        }
    }, [formData]);

    const formatBlurInputAmount = useCallback((value, setter) => {
        if (!value) return;
        const num = parseMontoSeguro(value);
        if (num > 0) setter(num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }, []);

    const handleProveedorChange = useCallback((e) => {
        const id = e.target.value;
        const found = proveedores.find(p => p.id === id);
        setFormData(prev => ({ ...prev, proveedorId: id, proveedorNombre: found ? (found.razonSocial || found.nombre) : '' }));
    }, [proveedores]);

    const handleAreaChange = useCallback((e) => {
        const id = e.target.value;
        const found = areas.find(a => a.id === id);
        setFormData(prev => ({ ...prev, areaId: id, areaNombre: found ? found.nombre : '' }));
    }, [areas]);

    const handleConceptoChange = useCallback((e) => {
        const id = e.target.value;
        const found = conceptosGastos.find(c => c.id === id);
        setFormData(prev => ({ ...prev, conceptoId: id, conceptoNombre: found ? found.nombre : '', subconceptoNombre: '' }));
        setSubconceptosDisponibles((found && Array.isArray(found.subconceptos)) ? found.subconceptos : []);
    }, [conceptosGastos]);

    const getNombreCuentaLegible = useCallback((found) => {
        if (!found) return '';
        return found.nombre || `${found.banco} • ${found.titular || ''} (${found.tipoCuenta || ''})`;
    }, []);

    const handleCuentaOrigenChange = useCallback((e) => {
        const id = e.target.value;
        const found = cuentas.find(c => c.id === id);
        setFormData(prev => ({ 
            ...prev, 
            cuentaId: id, 
            cuentaNombre: getNombreCuentaLegible(found) 
        }));
    }, [cuentas, getNombreCuentaLegible]);

    const handleAddCuotaBorrador = useCallback(() => {
        if (!cuotaInput.fecha) return setError("Ingrese la fecha de vencimiento de la cuota.");
        const numMonto = parseMontoSeguro(cuotaInput.monto);
        if (numMonto <= 0) return setError("El monto de la cuota debe ser mayor a 0.");

        setCuotasBorrador(prev => [
            ...prev, 
            { numero: prev.length + 1, fechaVencimiento: cuotaInput.fecha, monto: numMonto, pagada: false }
        ]);
        setCuotaInput(INITIAL_CUOTA_INPUT);
        setError(null);
    }, [cuotaInput]);

    const handleRemoveCuotaBorrador = useCallback((index) => {
        setCuotasBorrador(prev => prev.filter((_, idx) => idx !== index).map((c, idx) => ({ ...c, numero: idx + 1 })));
    }, []);

    const handleToggleCuotaPagada = useCallback((index) => {
        setFormData(prev => ({
            ...prev,
            cuotasPlan: prev.cuotasPlan.map((c, idx) => idx === index ? { ...c, pagada: !c.pagada } : c)
        }));
    }, []);

    const handleRemoveCuota = useCallback((index) => {
        setFormData(prev => ({
            ...prev,
            cuotasPlan: prev.cuotasPlan.filter((_, idx) => idx !== index).map((c, idx) => ({ ...c, numero: idx + 1 }))
        }));
    }, []);

    const handleAddPago = useCallback(() => {
        const forma = esModoDirecto ? formData.formaPagoImpuesto : pagoInput.forma;
        if (!forma) return setError("Seleccione una Forma de Pago.");

        if (forma === 'Cheque') {
            if (chequesBorrador.length === 0) return setError("Cargue al menos un cheque en la tanda antes de confirmar.");
            setChequesList(prev => [...prev, ...chequesBorrador]);
            setChequesBorrador([]);
            setPagoInput(prev => ({ ...prev, forma: '' }));
            setError(null);
            return;
        }

        if (formData.esPlanPago || (forma === 'Débito Automático' && cuotasBorrador.length > 0)) {
            if (cuotasBorrador.length === 0) return setError("Cargue al menos una cuota en la tanda antes de confirmar.");
            const targetId = esModoDirecto ? formData.cuentaId : pagoInput.cuentaId;
            const foundCta = cuentas.find(c => c.id === targetId);
            const cuentaNombre = getNombreCuentaLegible(foundCta);

            const cuotasConCuenta = cuotasBorrador.map(c => ({
                ...c,
                cuentaId: targetId || '',
                cuentaNombre: cuentaNombre || ''
            }));

            setFormData(prev => ({ ...prev, cuotasPlan: [...(prev.cuotasPlan || []), ...cuotasConCuenta] }));
            setCuotasBorrador([]);
            setError(null);
            return;
        }

        const numMonto = parseMontoSeguro(pagoInput.monto);
        if (numMonto <= 0) return setError("El monto debe ser mayor a 0.");

        let cuentaNombre = '';
        const formasConCuenta = ['Transferencia Bancaria', 'Tarjeta de Crédito', 'Débito Automático', 'Pagos mis cuentas', 'VEP'];
        const targetId = esModoDirecto ? formData.cuentaId : pagoInput.cuentaId;

        if (formasConCuenta.includes(forma) && targetId) {
            const foundCta = cuentas.find(c => c.id === targetId);
            cuentaNombre = getNombreCuentaLegible(foundCta);
        }

        setPagosList(prev => [...prev, { formaPago: forma, monto: numMonto, cuentaId: targetId || '', cuentaNombre, pagado: false }]);
        setPagoInput(INITIAL_PAGO_INPUT);
        setError(null);
    }, [esModoDirecto, formData.formaPagoImpuesto, formData.esPlanPago, formData.cuentaId, pagoInput, chequesBorrador, cuotasBorrador, cuentas, getNombreCuentaLegible]);

    const handleTogglePagoPagado = useCallback((index) => {
        setPagosList(prev => prev.map((p, idx) => idx === index ? { ...p, pagado: !p.pagado } : p));
    }, []);

    const handleRemovePago = useCallback((index) => setPagosList(prev => prev.filter((_, idx) => idx !== index)), []);

    const handleAddChequeBorrador = useCallback(() => {
        if (!chequeInput.numero.trim()) return setError("Ingrese el número de cheque.");
        if (!chequeInput.banco) return setError("Seleccione la cuenta/banco emisor del cheque.");
        
        const numMonto = parseMontoSeguro(chequeInput.monto);
        if (numMonto <= 0) return setError("El monto del cheque debe ser mayor a 0.");
        if (!chequeInput.vencimiento) return setError("Ingrese la fecha de vencimiento del cheque.");

        const foundCta = cuentas.find(c => c.id === chequeInput.banco || c.nombre === chequeInput.banco);

        setChequesBorrador(prev => [...prev, { 
            numero: chequeInput.numero.trim(), 
            monto: numMonto, 
            fechaVencimiento: chequeInput.vencimiento, 
            cuentaId: foundCta?.id || chequeInput.banco,
            cuentaNombre: getNombreCuentaLegible(foundCta) || chequeInput.banco,
            pagada: false
        }]);

        setChequeInput(INITIAL_CHEQUE_INPUT);
        setError(null);
    }, [chequeInput, cuentas, getNombreCuentaLegible]);

    const handleRemoveChequeBorrador = useCallback((index) => setChequesBorrador(prev => prev.filter((_, idx) => idx !== index)), []);
    const handleToggleChequePagados = useCallback((index) => setChequesList(prev => prev.map((chk, idx) => idx === index ? { ...chk, pagada: !chk.pagada } : chk)), []);
    const handleRemoveChequeDefinitivo = useCallback((index) => setChequesList(prev => prev.filter((_, idx) => idx !== index)), []);
    const handleRemoveTodosCheques = useCallback(() => setChequesList([]), []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.proveedorId && !formData.proveedorNombre) return setError("Debe seleccionar un Proveedor, Fisco u Entidad obligatoriamente.");
        if (calculos.montoTotal <= 0) return setError("El Monto Total debe ser mayor a 0.");

        setLoading(true);
        try {
            const chequesNormalizados = chequesList.map(c => ({
                numero: c.numero,
                monto: c.monto,
                fechaVencimiento: c.fechaVencimiento,
                cuentaId: c.cuentaId || c.bancoId || '',
                cuentaNombre: c.cuentaNombre || c.banco || '',
                pagada: !!c.pagada
            }));

            const cuotasNormalizadas = (formData.cuotasPlan || []).map(c => ({
                numero: c.numero,
                monto: c.monto,
                fechaVencimiento: c.fechaVencimiento,
                cuentaId: c.cuentaId || '',
                cuentaNombre: c.cuentaNombre || '',
                pagada: !!c.pagada
            }));

            const { cuentaId, cuentaNombre, cuentaIdOrigen, cuentaNombreOrigen, formaPagoImpuesto, ...restoFormData } = formData;

            const estatusDefinitivo = calculos.estatusCalculado === 'Pagado' ? 'Pagado' : (formData.estatus || calculos.estatusCalculado);

            const payload = {
                ...restoFormData,
                estatus: estatusDefinitivo,
                moneda: formData.moneda,
                tasaIva: calculos.tasaFinal,
                montoNeto: calculos.neto,
                montoIva: calculos.montoIva,
                totalPercepciones: calculos.totalPercepciones,
                montoOtrosTributos: calculos.montoOtrosTributos,
                montoTotal: calculos.montoTotal,
                pagosList,
                chequesList: chequesNormalizados,
                cuotasPlan: cuotasNormalizadas,
                montoEgresoBanco: calculos.egresoRealBanco,
                diferenciaEgreso: calculos.diferenciaEgreso
            };

            await saveGastoCompra(payload, initialData?.id || null);
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error("Error al guardar comprobante:", err);
            setError("Error técnico al intentar registrar el comprobante.");
        } finally {
            setLoading(false);
        }
    };

    return {
        formData, setFormData,
        subconceptosDisponibles,
        pagosList,
        pagoInput, setPagoInput,
        chequesList, chequesBorrador,
        chequeInput, setChequeInput,
        cuotasBorrador,
        cuotaInput, setCuotaInput,
        loading, error,
        esImpuesto, esPrestamo, esModoDirecto,
        calculos, proveedores, cuentas, areas, conceptosGastos,
        handleBlurCurrency, formatBlurInputAmount,
        handleProveedorChange, handleAreaChange, handleConceptoChange, handleCuentaOrigenChange,
        handleAddCuotaBorrador, handleRemoveCuotaBorrador, handleToggleCuotaPagada, handleRemoveCuota,
        handleAddPago, handleTogglePagoPagado, handleRemovePago,
        handleAddChequeBorrador, handleRemoveChequeBorrador, handleToggleChequePagados, handleRemoveChequeDefinitivo, handleRemoveTodosCheques,
        handleSubmit
    };
};